import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Tooltip,
  Divider,
  Box,
  Button,
  FormControlLabel,
  Switch,
  ButtonGroup,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import MuiLink from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import SpaIcon from '@mui/icons-material/Spa';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InventoryIcon from '@mui/icons-material/Inventory';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import logo from '../micgromanlogo.svg';
import HygroLogo from '../hygrologo.svg';
import UserAvatar from './UserAvatar';
import {
  normalizeInviteCode,
  formatInviteCode,
  validateInviteCode,
} from '../utils/inviteCode';
import { canAccessDevRulesTest } from '../utils/devRulesTestAccess';
import { listMyFarmsClient } from '../utils/listMyFarmsClient';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// Helper: truncate long names
function truncate(str, maxLength) {
  if (!str) return '';
  return str.length > maxLength ? str.slice(0, maxLength) + '…' : str;
}

const NavigationMenu = ({
  darkMode,
  setDarkMode,
  user,
  userData,
  farmData,
  loading,
  onOpenSignIn,
  onOpenSignUp,
  onLogout,
  onSubmitInviteCode,
  onSelectFarm,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const [farmMenuAnchorEl, setFarmMenuAnchorEl] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteCodeNormalized, setInviteCodeNormalized] = useState('');
  const [activeMemberRole, setActiveMemberRole] = useState(null);
  const [memberFarms, setMemberFarms] = useState([]);

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);
  const toggleAccountDrawer = () => setAccountDrawerOpen(!accountDrawerOpen);

  const handleToggleDarkMode = () => setDarkMode(!darkMode);
  const handleLogoClick = () => navigate('/');
  const handleLogoutClick = () => { setAccountDrawerOpen(false); onLogout(); };
  const handleAccountPageClick = () => { setAccountDrawerOpen(false); navigate('/account'); };
  const handleCodeGenClick = () => { setAccountDrawerOpen(false); navigate('/codegen'); };
  const handleDevRulesTestClick = () => { setAccountDrawerOpen(false); setDrawerOpen(false); navigate('/dev-rules-test'); };
  const handleHelpClick = () => { setAccountDrawerOpen(false); navigate('/help'); };
  const handleMobileSignIn = () => { setDrawerOpen(false); onOpenSignIn(); };
  const handleMobileSignUp = () => { setDrawerOpen(false); onOpenSignUp(); };
  const handleOpenFarmMenu = (event) => setFarmMenuAnchorEl(event.currentTarget);
  const handleCloseFarmMenu = () => setFarmMenuAnchorEl(null);
  const handleOpenInviteDialog = () => {
    setInviteCodeNormalized('');
    setInviteDialogOpen(true);
  };
  const handleCloseInviteDialog = () => setInviteDialogOpen(false);
  const handleSubmitInvite = async () => {
    const normalized = normalizeInviteCode(inviteCodeNormalized);
    if (!validateInviteCode(normalized)) return;
    const ok = await onSubmitInviteCode?.(normalized);
    if (ok !== false) {
      setInviteDialogOpen(false);
      setInviteCodeNormalized('');
    }
  };
  const handleSelectFarm = (farmId) => {
    if (!farmId) return;
    onSelectFarm?.(farmId);
    handleCloseFarmMenu();
    setAccountDrawerOpen(false);
    setDrawerOpen(false);
  };

  const paths = ['dashboard', 'harvests', 'orders', 'tasks', 'management'];
  const farmLogoUrl = farmData?.logoUrl;
  const activeFarmId = userData?.activeFarmId;
  const hasMultipleFarms = memberFarms.length > 1;
  const activeFarm = memberFarms.find((farm) => farm.id === activeFarmId) || memberFarms[0] || null;

  useEffect(() => {
    if (!user?.uid) {
      setMemberFarms([]);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const farms = await listMyFarmsClient();
        if (cancelled) return;
        setMemberFarms(farms);
      } catch (error) {
        console.warn('listMyFarms failed:', error);
        if (!cancelled) {
          setMemberFarms([]);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, userData?.activeFarmId]);

  useEffect(() => {
    if (!user?.uid || !activeFarmId) {
      setActiveMemberRole(null);
      return;
    }
    const memberRef = doc(db, 'farms', activeFarmId, 'members', user.uid);
    const unsubscribe = onSnapshot(
      memberRef,
      (snap) => {
        setActiveMemberRole(snap.exists() ? snap.data()?.role || null : null);
      },
      () => setActiveMemberRole(null)
    );
    return () => unsubscribe();
  }, [user?.uid, activeFarmId]);

  const roleLabel = (role) => {
    if (!role) return 'No Access';
    const lower = String(role).toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  const roleChipSx = (role) => {
    const lower = String(role || 'member').toLowerCase();
    if (lower === 'owner') {
      return {
        borderColor: 'tertiary.main',
        color: 'common.white',
        bgcolor: 'tertiary.main',
      };
    }
    if (lower === 'admin') {
      return {
        borderColor: 'warning.dark',
        color: 'common.white',
        bgcolor: 'warning.dark',
      };
    }
    if (lower === 'worker') {
      return {
        borderColor: 'secondary.dark',
        color: 'common.white',
        bgcolor: 'secondary.dark',
      };
    }
	    return {
	      borderColor: 'primary.dark',
	      color: 'common.white',
	      bgcolor: 'primary.dark',
	    };
	  };

  const activeRoleRaw =
    activeMemberRole
    || activeFarm?.role
    || (farmData?.createdBy && user?.uid && farmData.createdBy === user.uid ? 'owner' : null);

  const activeRoleLabel = roleLabel(activeRoleRaw);
  const showDevRulesTestLink = canAccessDevRulesTest(user, userData);

  const renderFarmTile = (src, size, grouped = false, role = null, roleRaw = null, chipInset = 6) => (
    <Box
      sx={{
        width: size,
        height: size,
        boxSizing: 'border-box',
        position: 'relative',
        borderRadius: grouped ? '8px 0 0 8px' : 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt="Farm logo"
          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
        />
      ) : null}
      {role ? (
        <Box sx={{ position: 'absolute', left: chipInset, top: chipInset, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Chip
            size="small"
            variant="outlined"
            label={role}
            sx={{
              height: 20,
              '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
              bgcolor: 'background.paper',
              ...roleChipSx(roleRaw),
            }}
          />
        </Box>
      ) : null}
    </Box>
  );

  const handleAddFarm = () => {
    handleCloseFarmMenu();
    handleOpenInviteDialog();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Desktop AppBar (hidden on xs/sm) */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper,
          display: { xs: 'none', md: 'flex' },
        }}
      >
        <Toolbar
          sx={
            user
              ? {
                  display: 'grid',
                  gridTemplateColumns: '35% 30% 35%',
                  alignItems: 'center',
                }
              : {
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }
          }
        >
          {/* Desktop: farm logo + name */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1,
              m: 2,
              width: '95%',
              minWidth: 0,
            }}
          >
            {farmLogoUrl && (
              <Box
                sx={{
                  flex: '0 0 auto',
                  maxWidth: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <img
                  src={farmLogoUrl}
                  alt="Farm Logo"
                  style={{
                    height: 100,
                    width: 'auto',
                    maxWidth: 200,
                    objectFit: 'contain',
                    objectPosition: 'left center',
                    cursor: 'pointer',
                    display: 'block',
                  }}
                  onClick={handleLogoClick}
                />
              </Box>
            )}
            {(farmData?.farmName || userData?.farmName) && (
	              <Typography
	                variant="subtitle1"
	                title={farmData?.farmName || userData?.farmName}
	                sx={{
	                  color: (theme) => theme.palette.text.primary,
	                  fontWeight: 600,
	                  fontSize: '2rem',
	                  ml: 1.5,
	                  flex: 1,
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {farmData?.farmName || userData?.farmName}
              </Typography>
            )}
          </Box>

          {/* Desktop nav links (center column kept to preserve right alignment) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 1,
              pb: 1.5,
              width: '100%',
            }}
          >
            {userData?.approved ? (
              <>
                <Box
                  component="img"
                  src={logo}
                  alt="MicGro Manager logo"
                  onClick={handleLogoClick}
                  sx={{
                    width: 130,
                    height: 'auto',
                    cursor: 'pointer',
                    filter: darkMode ? 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.35))' : 'none',
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 3,
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  {paths.map((path) => (
                    <Link key={path} to={`/${path}`} style={{ textDecoration: 'none' }}>
                      <Typography
                        color="textPrimary"
                        textTransform="capitalize"
                        sx={{
                          fontSize: '1.2rem',
                          fontWeight: location.pathname.startsWith(`/${path}`) ? 700 : 500,
                          borderBottom: '2px solid',
                          borderColor: location.pathname.startsWith(`/${path}`) ? 'tertiary.main' : 'transparent',
                          pb: 0.25,
                        }}
                      >
                        {path}
                      </Typography>
                    </Link>
                  ))}
                </Box>
              </>
            ) : null}
          </Box>

          {/* Desktop-only user/spinner/auth */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              minWidth: 0,
            }}
          >
            {loading ? (
              <CircularProgress color="inherit" size={24} />
            ) : user ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5 }}>
                  <IconButton
                    onClick={toggleAccountDrawer}
                    sx={{
                      backgroundColor: 'transparent',
                      color: 'inherit',
                      borderRadius: '50%',
                      p: 0,
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                  >
                    <UserAvatar user={user} userData={userData} size={50} />
                  </IconButton>
                  <Typography
                    variant="body1"
                    align="center"
                    sx={{
                      mt: 1,
                      color: darkMode ? 'white' : 'black',
                      textAlign: 'center',
                      whiteSpace: 'normal',
                    }}
                  >
                    {truncate(userData?.displayName || user.email, 15)}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <ButtonGroup variant="contained" orientation="horizontal" sx={{ whiteSpace: 'nowrap' }}>
                <Button
                  endIcon={<AssignmentIcon />}
                  size="large"
                  color="secondary"
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={onOpenSignUp}
                >
                  Sign Up
                </Button>
                <Button
                  endIcon={<AccountCircle />}
                  size="large"
                  color="primary"
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={onOpenSignIn}
                >
                  Login
                </Button>
              </ButtonGroup>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Floating mobile-only hamburger button */}
      <IconButton
        onClick={toggleDrawer}
        sx={{
          display: { xs: 'flex', md: 'none' },     // only on mobile
          position: 'fixed',                       // fix it in place
          top: 16,                                  // 16px from the top
          right: 16,                                // 16px from the right
          zIndex: theme.zIndex.drawer - 1,         // above the drawer
          backgroundColor: 'quaternary.main',
          color: 'white',
          '&:hover': { backgroundColor: 'secondary.dark' },
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Desktop account drawer (right) */}

      <Drawer
        anchor="right"
        open={accountDrawerOpen}
        onClose={() => setAccountDrawerOpen(false)}
        PaperProps={{ sx: { width: 250 } }}
      >
        <Box
          role="presentation"
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {/* Farm picker */}
          {(farmLogoUrl || !loading) && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                {renderFarmTile(farmLogoUrl || activeFarm?.logoUrl, 170, true, activeRoleLabel, activeRoleRaw, 10)}
                <Tooltip title={hasMultipleFarms ? 'Switch or add farm' : 'Add farm'}>
                  <IconButton
                    size="small"
                    onClick={hasMultipleFarms ? handleOpenFarmMenu : handleAddFarm}
                    sx={{
                      ml: 0,
                      width: 36,
                      minWidth: 36,
                      height: 170,
                      boxSizing: 'border-box',
                      borderRadius: '0 8px 8px 0',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderLeftWidth: 1,
                      bgcolor: 'action.hover',
                      color: (theme) => (theme.palette.mode === 'light' ? 'common.black' : 'common.white'),
                      transition: 'background-color 0.15s ease, color 0.15s ease, transform 0.08s ease',
                      '&:hover': {
                        bgcolor: 'action.selected',
                        color: (theme) => (theme.palette.mode === 'light' ? 'common.black' : 'common.white'),
                      },
                      '&:active': {
                        bgcolor: 'action.selected',
                        color: (theme) => (theme.palette.mode === 'light' ? 'common.black' : 'common.white'),
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    {hasMultipleFarms ? <ExpandMoreIcon /> : <AddIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Username */}
          {!loading && user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <UserAvatar user={user} userData={userData} size={50} />
              <Typography variant="body1">
                {truncate(userData?.displayName || user.email, 15)}
              </Typography>
            </Box>
          )}

          <Divider sx={{ width: '100%', my: 2 }} />

          {/* Account / Help / Logout */}
          <List sx={{ width: '100%' }}>
            <ListItem button onClick={handleAccountPageClick}>
              <ListItemText primary="Account" />
            </ListItem>
            {userData?.isDeveloper && userData?.approved && (
              <ListItem button onClick={handleCodeGenClick}>
                <ListItemText primary="Code Generator" />
              </ListItem>
            )}
            <ListItem button onClick={handleHelpClick}>
              <ListItemText primary="Help" />
            </ListItem>
            <ListItem button onClick={handleLogoutClick}>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>

          <Divider sx={{ width: '100%', my: 2 }} />

          {/* Dark/Light Mode segmented toggle */}
          <ToggleButtonGroup
            value={darkMode ? 'dark' : 'light'}
            exclusive
            onChange={(_, val) => val !== null && setDarkMode(val === 'dark')}
            size="small"
            aria-label="theme mode toggle"
            sx={{
              borderRadius: '24px',
              overflow: 'hidden',
              alignSelf: 'flex-start',
              px: 4,
            }}
          >
            <ToggleButton
              value="light"
              aria-label="light mode"
              sx={{
                px: 2.5,
                borderRadius: 0,
                '&:first-of-type': { borderRadius: '24px 0 0 24px' },
                '&:not(.Mui-selected)': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  color: (theme) => theme.palette.warning.main,
                  backgroundColor: 'transparent',
                },
              }}
            >
              <LightModeIcon />
            </ToggleButton>

            <ToggleButton
              value="dark"
              aria-label="dark mode"
              sx={{
                px: 2.5,
                borderRadius: 0,
                '&:last-of-type': { borderRadius: '0 24px 24px 0' },
                '&:not(.Mui-selected)': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  color: (theme) => theme.palette.warning.main,
                  backgroundColor: 'transparent',
                },
              }}
            >
              <DarkModeIcon />
            </ToggleButton>
          </ToggleButtonGroup>

          {showDevRulesTestLink ? (
            <List sx={{ width: '100%', mt: 1 }}>
              <ListItem button onClick={handleDevRulesTestClick}>
                <ListItemText primary="Dev Rules Test" />
              </ListItem>
            </List>
          ) : null}

          <Box component="footer" sx={{ mt: 'auto', textAlign: 'center', p: 1 }}>
            <img
              src={logo}
              alt="Logo"
              style={{
                width: 200,
                height: 'auto',
                filter: darkMode ? 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.35))' : 'none',
              }}
            />
            <Divider sx={{ width: '100%', my: 2 }} />
            <MuiLink
              href="https://hy-gro.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              <img 
                src={HygroLogo}
                alt="Hy-Gro Logo" 
                style={{ 
                  width: 100, 
                  height: 'auto',
                  filter: darkMode ? 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.35))' : 'none',
                }} />
            </MuiLink>
            <Typography variant="body2" sx={{ fontSize: '0.65rem' }}>
              © {new Date().getFullYear()} Hy-Gro Hydroponics LTD
            </Typography>
          </Box>
        </Box>
      </Drawer>


      {/* Mobile unified drawer (left) */}
      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer}>
        <Box
          role="presentation"
          sx={{
            width: 250,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
        <Box
            sx={{
              mt: 1,
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {(farmLogoUrl || !loading) && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                  {renderFarmTile(farmLogoUrl || activeFarm?.logoUrl, 170, true, activeRoleLabel, activeRoleRaw, 6)}
                  <Tooltip title={hasMultipleFarms ? 'Switch or add farm' : 'Add farm'}>
                    <IconButton
                      size="small"
                      onClick={hasMultipleFarms ? handleOpenFarmMenu : handleAddFarm}
                      sx={{
                        ml: 0,
                        width: 36,
                        minWidth: 36,
                        height: 170,
                        boxSizing: 'border-box',
                        borderRadius: '0 8px 8px 0',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderLeftWidth: 1,
                        bgcolor: 'action.hover',
                        color: (theme) => (theme.palette.mode === 'light' ? 'common.black' : 'common.white'),
                        transition: 'background-color 0.15s ease, color 0.15s ease, transform 0.08s ease',
                        '&:hover': {
                          bgcolor: 'action.selected',
                          color: (theme) => (theme.palette.mode === 'light' ? 'common.black' : 'common.white'),
                        },
                        '&:active': {
                          bgcolor: 'action.selected',
                          color: (theme) => (theme.palette.mode === 'light' ? 'common.black' : 'common.white'),
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      {hasMultipleFarms ? <ExpandMoreIcon /> : <AddIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
            {!loading && user && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <UserAvatar user={user} userData={userData} size={50} />
                <Typography variant="body1">
                  {truncate(userData?.displayName || user.email, 15)}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Nav links with icons */}
          <List sx={{ px: 2 }}>
            {user &&
              userData?.approved &&
              paths.map((path) => {
                let IconComponent;
                switch (path) {
                  case 'dashboard':
                    IconComponent = SpaceDashboardIcon;
                    break;
                  case 'harvests':
                    IconComponent = SpaIcon;
                    break;
                  case 'orders':
                    IconComponent = LocalShippingIcon;
                    break;
                  case 'tasks':
                    IconComponent = ListAltIcon;
                    break;
                  case 'management':
                    IconComponent = InventoryIcon;
                    break;
                  default:
                    IconComponent = SpaceDashboardIcon;
                }
                const isActive = location.pathname === `/${path}`;
                return (
                  <ListItem
                    key={path}
                    button
                    component={Link}
                    to={`/${path}`}
                    onClick={toggleDrawer}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive
                          ? theme.palette.secondary.main
                          : 'inherit',
                      }}
                    >
                      <IconComponent />
                    </ListItemIcon>
                    <ListItemText
                      primary={path.charAt(0).toUpperCase() + path.slice(1)}
                    />
                  </ListItem>
                );
              })}
          </List>

          <Divider />

          {/* Account actions or auth */}
          <List sx={{ px: 2 }}>
            {!loading && user ? (
              <>
                <ListItem
                  button
                  onClick={() => {
                    toggleDrawer();
                    navigate('/account');
                  }}
                >
                  <ListItemText primary="Account" />
                </ListItem>
                {userData?.isDeveloper && (
                  <ListItem
                    button
                    onClick={() => {
                      toggleDrawer();
                      navigate('/codegen');
                    }}
                  >
                    <ListItemText primary="Code Generator" />
                  </ListItem>
                )}
                <ListItem
                  button
                  onClick={() => {
                    toggleDrawer();
                    navigate('/help');
                  }}
                >
                  <ListItemText primary="Help" />
                </ListItem>
                <ListItem
                  button
                  onClick={() => {
                    toggleDrawer();
                    onLogout();
                  }}
                >
                  <ListItemText primary="Logout" />
                </ListItem>
              </>
            ) : (
              <ListItem>
                <ButtonGroup
                  variant="contained"
                  orientation="vertical"
                  sx={{ width: '100%' }}
                >
                  <Button onClick={handleMobileSignUp} fullWidth sx={{ mb: 1 }}>
                    Sign Up
                  </Button>
                  <Button onClick={handleMobileSignIn} fullWidth>
                    Login
                  </Button>
                </ButtonGroup>
              </ListItem>
            )}
          </List>

        {/* Dark/Light Mode segmented toggle */}
        <ToggleButtonGroup
          value={darkMode ? 'dark' : 'light'}
          exclusive
          onChange={(_, val) => val !== null && setDarkMode(val === 'dark')}
          size="small"
          aria-label="theme mode toggle"
          sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            alignSelf: 'flex-start',
            px: 4,
            mb: 2,
          }}
        >
          <ToggleButton
            value="light"
            aria-label="light mode"
            sx={{
              px: 1.5,
              borderRadius: 0,
              '&:first-of-type': { borderRadius: '24px 0 0 24px' },
              '&:not(.Mui-selected)': {
                backgroundColor: (theme) => theme.palette.action.hover,
              },
              '&.Mui-selected': {
                color: (theme) => theme.palette.primary.main,
                backgroundColor: 'transparent',
              },
            }}
          >
            <LightModeIcon />
          </ToggleButton>

          <ToggleButton
            value="dark"
            aria-label="dark mode"
            sx={{
              px: 1.5,
              borderRadius: 0,
              '&:last-of-type': { borderRadius: '0 24px 24px 0' },
              '&:not(.Mui-selected)': {
                backgroundColor: (theme) => theme.palette.action.hover,
              },
              '&.Mui-selected': {
                color: (theme) => theme.palette.primary.main,
                backgroundColor: 'transparent',
              },
            }}
          >
            <DarkModeIcon />
          </ToggleButton>
        </ToggleButtonGroup>

          {showDevRulesTestLink ? (
            <List sx={{ width: '100%', mt: 1 }}>
              <ListItem
                button
                onClick={() => {
                  toggleDrawer();
                  navigate('/dev-rules-test');
                }}
              >
                <ListItemText primary="Dev Rules Test" />
              </ListItem>
            </List>
          ) : null}

          <Divider />

          {/* Footer, pushed to bottom */}
          <Box component="footer" sx={{ mt: 'auto', textAlign: 'center', p: 2 }}>
            <img
              src={logo}
              alt="Logo"
              style={{
                width: 120,
                height: 'auto',
                filter: darkMode ? 'drop-shadow(0 3px 8px rgba(0, 0, 0, 0.35))' : 'none',
              }}
            />
            <Typography variant="body2" sx={{ fontSize: '0.65rem', mt: 1 }}>
              © {new Date().getFullYear()} Hy-Gro Hydroponics LTD
              <br />
              <MuiLink
                href="https://hy-gro.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  color: 'secondary.main',
                  '&:visited': { color: 'warning.main' },
                }}
              >
                Click Here
              </MuiLink>
            </Typography>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={inviteDialogOpen} onClose={handleCloseInviteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Join a Farm</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Invite code"
            fullWidth
            value={formatInviteCode(inviteCodeNormalized)}
            onChange={(e) => setInviteCodeNormalized(normalizeInviteCode(e.target.value).slice(0, 15))}
            helperText={
              inviteCodeNormalized && !validateInviteCode(inviteCodeNormalized)
                ? 'Code should be 15 characters'
                : ' '
            }
            error={Boolean(inviteCodeNormalized) && !validateInviteCode(inviteCodeNormalized)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitInvite}
            variant="contained"
            disabled={!validateInviteCode(inviteCodeNormalized)}
          >
            Join
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={farmMenuAnchorEl}
        open={Boolean(farmMenuAnchorEl) && hasMultipleFarms}
        onClose={handleCloseFarmMenu}
      >
        {memberFarms.map((farm) => (
          <MenuItem
            key={farm.id}
            onClick={() => handleSelectFarm(farm.id)}
            aria-label={farm.name || 'Farm'}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 96 }}
          >
            <Box
              sx={
                farm.id === activeFarmId
                  ? { filter: 'grayscale(1)', opacity: 0.6 }
                  : undefined
              }
            >
              {renderFarmTile(farm.logoUrl, 84, false, roleLabel(farm.role), farm.role, 6)}
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={handleAddFarm}
          sx={{
            justifyContent: 'center',
            minHeight: 40,
            py: 0.25,
            borderRadius: 1,
            mx: 1,
            bgcolor: 'transparent',
            transition: 'background-color 0.15s ease, color 0.15s ease, transform 0.08s ease',
            '&:hover': {
              bgcolor: 'action.selected',
            },
            '&:active': {
              bgcolor: 'action.selected',
              transform: 'scale(0.99)',
            },
          }}
          aria-label="Add farm"
        >
          <Tooltip title="Add farm">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <AddIcon
                fontSize="small"
                sx={{ color: (theme) => (theme.palette.mode === 'light' ? 'common.black' : 'common.white') }}
              />
            </Box>
          </Tooltip>
        </MenuItem>
      </Menu>


    </Box>
  );
};

export default NavigationMenu;





